import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { QuantitySelector } from './QuantitySelector';
import { formatPrice, calculateDiscountPercentage, getBulkDiscountInfo, getNextTierSavings } from '@/lib/pricing';
import { ShoppingCart, Sparkles, TrendingUp, Gift } from 'lucide-react';
import useCartStore from '@/store/cartStore';

export function ProductDetail({ product }) {
  const [quantity, setQuantity] = useState(1);
  const addItem = useCartStore((state) => state.addItem);
  const [showAddedMessage, setShowAddedMessage] = useState(false);

  const discountPercentage = calculateDiscountPercentage(
    product.pricing.originalPrice,
    product.pricing.salePrice
  );

  const bulkDiscountInfo = getBulkDiscountInfo(quantity, product.bulkDiscounts);
  
  // 計算當前數量的價格
  const subtotal = product.pricing.salePrice * quantity;
  const discountedPrice = bulkDiscountInfo.applicable 
    ? Math.round(subtotal * bulkDiscountInfo.rate)
    : subtotal;
  const savings = subtotal - discountedPrice;
  
  // 計算下一級優惠可以省下的金額
  const nextTierInfo = getNextTierSavings(
    quantity,
    product.pricing.salePrice,
    product.bulkDiscounts
  );

  const handleAddToCart = () => {
    addItem(product, quantity);
    setShowAddedMessage(true);
    setTimeout(() => setShowAddedMessage(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* 產品標題 */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          {product.name}
        </h1>
        <p className="text-lg text-gray-600">{product.subtitle}</p>
        
        <div className="flex flex-wrap gap-2 mt-4">
          {product.tags.map((tag, index) => (
            <Badge key={index} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      </div>

      {/* 價格區 */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-baseline gap-3">
              {discountPercentage > 0 && (
                <span className="text-xl text-gray-400 line-through">
                  {formatPrice(product.pricing.originalPrice)}
                </span>
              )}
              <span className="text-4xl font-bold text-blue-600">
                {formatPrice(product.pricing.salePrice)}
              </span>
              {discountPercentage > 0 && (
                <Badge className="bg-red-500">
                  省 {discountPercentage}%
                </Badge>
              )}
            </div>
            
            {/* 團購優惠提示 */}
            {Object.keys(product.bulkDiscounts).length > 0 && (
              <Alert className="border-green-200 bg-green-50">
                <Sparkles className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <span className="font-semibold">團購優惠：</span>
                  1件8折、2件7折、3件6折、6件以上5折！
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 數量選擇與加入購物車 */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* 數量選擇器 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                選擇數量
              </label>
              <QuantitySelector
                value={quantity}
                onChange={setQuantity}
                min={product.stock.minPurchaseQuantity}
                max={product.stock.maxPurchaseQuantity}
              />
            </div>

            {/* 當前數量的價格計算 */}
            {quantity > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">小計</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                
                {bulkDiscountInfo.applicable && (
                  <>
                    <div className="flex justify-between text-sm text-green-600">
                      <span>團購優惠（{bulkDiscountInfo.message}）</span>
                      <span className="font-medium">-{formatPrice(savings)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-blue-600 border-t pt-2">
                      <span>優惠價</span>
                      <span>{formatPrice(discountedPrice)}</span>
                    </div>
                    {quantity > 0 && (
                      <div className="text-xs text-green-700 pt-1">
                        💰 已為您節省 {formatPrice(savings)}
                      </div>
                    )}
                  </>
                )}
                
                {!bulkDiscountInfo.applicable && bulkDiscountInfo.message && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <TrendingUp className="h-4 w-4" />
                    <span>{bulkDiscountInfo.message}</span>
                  </div>
                )}
              </div>
            )}
            
            {/* 升級優惠提示 */}
            {nextTierInfo && (
              <Alert className="border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
                <TrendingUp className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-sm">
                  <div className="space-y-1">
                    <p className="font-semibold text-orange-900">
                      🎁 再加購 {nextTierInfo.needToBuy} 件，享更多優惠！
                    </p>
                    <p className="text-orange-800">
                      升級至 {nextTierInfo.nextThreshold} 件享 <strong>{Math.round(nextTierInfo.nextRate * 10)} 折</strong>，
                      <strong className="text-orange-600"> 可再省 {formatPrice(nextTierInfo.savings)}</strong>
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {/* 已享有最高優惠提示 */}
            {!nextTierInfo && bulkDiscountInfo.applicable && (
              <Alert className="border-green-200 bg-green-50">
                <Gift className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-sm text-green-800">
                  🎉 恭喜！您已選擇最高團購優惠（{Math.round(bulkDiscountInfo.rate * 10)} 折）
                </AlertDescription>
              </Alert>
            )}

            {/* 加入購物車按鈕 */}
            <div className="space-y-2">
              <Button 
                className="w-full h-12 text-lg" 
                onClick={handleAddToCart}
                disabled={!product.stock.available}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                {product.stock.available ? '加入購物車' : '目前缺貨'}
              </Button>
              
              {showAddedMessage && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800 text-center">
                    已成功加入 {quantity} 件到購物車！
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* 庫存狀態 */}
            {product.stock.available && product.stock.quantity <= product.stock.lowStockThreshold && (
              <Alert variant="destructive">
                <AlertDescription>
                  僅剩 {product.stock.quantity} 件，欲購從速！
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 產品亮點 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {product.features.map((feature, index) => (
          <Card key={index} className="text-center">
            <CardContent className="pt-6">
              <div className="text-3xl mb-2">{feature.icon}</div>
              <div className="font-semibold text-sm">{feature.title}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
